import { useEffect, useReducer, useRef } from 'react';

import { trackEvent } from '@zep/logger/tracking';

import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import {
  AI_GENERATE_DOCK_ONBOARDING_START_DELAY_MS,
  type AiGenerateDockOnboardingStep,
} from '../consts/aiGenerateDockOnboarding.const';
import { createAiBuilderClientRequestId } from '../stores/aiBuilderTurnRequests';
import {
  type AiGenerateDockOnboardingEndReason,
  type AiGenerateDockOnboardingState,
  isLastAiGenerateDockOnboardingStep,
  reduceAiGenerateDockOnboarding,
  shouldRememberAiGenerateDockOnboarding,
} from '../utils/aiGenerateDockOnboarding';
import { aiGenerateDockOnboardingStorage } from '../utils/aiGenerateDockOnboardingStorage';
import { type AiGeneratorOpenedPlace } from '../utils/events.quiz-ai-generator';

const initialState = (): AiGenerateDockOnboardingState =>
  aiGenerateDockOnboardingStorage.hasSeen()
    ? { phase: 'ended' }
    : { phase: 'waiting' };

/** 계측 문맥 — 찍는 순간의 값을 쓴다(시작은 타이머 안이라 렌더 값이 아니라 최신 값을 읽는다). */
type OnboardingTrackingContext = {
  place: AiGeneratorOpenedPlace;
  sourceType: AiGenerateDockSourceType;
};

type OnboardingAttempt = { id: string; startedAt: number };

const trackCoachmarkView = (
  context: OnboardingTrackingContext,
  attempt: OnboardingAttempt,
  stage: AiGenerateDockOnboardingStep,
) =>
  trackEvent('quiz_ai_generator_coachmark_view', {
    place: context.place,
    stage,
    source_type: context.sourceType,
    attempt_id: attempt.id,
  });

/**
 * 생성기 코치마크 진행 — 처음으로 보일 수 있게 되면(eligible) 여는 모션 뒤에 1단계를 띄우고, 끝나면 브라우저에 기억한다.
 * 보이는 중 eligible 이 꺼지면(안내가 뜸·입력창이 숨겨짐) 단계는 그대로 두고 숨기기만 하며, 다시 켜지면 그 단계부터 잇는다.
 * 단계가 처음 보일 때·끝날 때 한 번씩 찍는다(전이 자리에서만 — 숨겼다 다시 보여도 다시 찍지 않는다).
 */
export const useAiGenerateDockOnboarding = ({
  eligible,
  place,
  sourceType,
}: {
  eligible: boolean;
} & OnboardingTrackingContext) => {
  const [state, dispatch] = useReducer(
    reduceAiGenerateDockOnboarding,
    undefined,
    initialState,
  );
  const waiting = state.phase === 'waiting';
  const trackingRef = useRef<OnboardingTrackingContext>({ place, sourceType });
  useEffect(() => {
    trackingRef.current = { place, sourceType };
  }, [place, sourceType]);
  /** 코치마크 한 번 — 시작할 때 만든다. */
  const attemptRef = useRef<OnboardingAttempt | null>(null);

  useEffect(() => {
    if (!eligible || !waiting) return;
    const id = setTimeout(() => {
      const attempt = {
        id: createAiBuilderClientRequestId(),
        startedAt: Date.now(),
      };
      attemptRef.current = attempt;
      dispatch({ type: 'start' });
      trackCoachmarkView(trackingRef.current, attempt, 'input');
    }, AI_GENERATE_DOCK_ONBOARDING_START_DELAY_MS);
    return () => clearTimeout(id);
  }, [eligible, waiting]);

  const showing = state.phase === 'showing' ? state.step : null;

  /**
   * 보이는 중에만 끝낸다 — 띄우기 전에 입력창을 닫았다면 다음에 열 때 다시 기회를 준다.
   * ✕ 닫기는 기억하지 않고 기다림으로 돌려, 다음에 열면 1단계부터 다시 띄운다.
   */
  const end = (reason: AiGenerateDockOnboardingEndReason) => {
    if (!showing) return;
    if (shouldRememberAiGenerateDockOnboarding(reason)) {
      aiGenerateDockOnboardingStorage.markSeen();
      dispatch({ type: 'end' });
    } else {
      dispatch({ type: 'rewind' });
    }
    if (attemptRef.current) {
      trackEvent('quiz_ai_generator_coachmark_end', {
        place: trackingRef.current.place,
        stage: showing,
        end_reason: reason,
        source_type: trackingRef.current.sourceType,
        attempt_id: attemptRef.current.id,
        elapsed_ms: Date.now() - attemptRef.current.startedAt,
      });
      attemptRef.current = null;
    }
  };

  const next = () => {
    if (!showing) return;
    if (isLastAiGenerateDockOnboardingStep(showing)) {
      end('complete');
      return;
    }
    const nextState = reduceAiGenerateDockOnboarding(state, { type: 'next' });
    dispatch({ type: 'next' });
    if (nextState.phase === 'showing' && attemptRef.current) {
      trackCoachmarkView(
        trackingRef.current,
        attemptRef.current,
        nextState.step,
      );
    }
  };

  const step: AiGenerateDockOnboardingStep | null = eligible ? showing : null;

  return { step, next, end };
};
